import {
  isWorkspaceTimedMediaFileAccepted,
  resolveWorkspaceTimedMediaFileMimeType,
} from '@/ai/media/timed-media-input-policy';
import {
  type BrowserUploadProgress,
  uploadFileFromBrowser,
} from '@/storage/client';

type MediaUploadGuestTask = Parameters<typeof uploadFileFromBrowser>[2];

export type MediaUploadSizeViolation = {
  maxBytes: number;
  sizeBytes: number;
};

export function resolveMediaUploadSizeViolation(
  file: Pick<File, 'size'>,
  maxBytes?: number
): MediaUploadSizeViolation | null {
  if (
    typeof maxBytes !== 'number' ||
    !Number.isFinite(maxBytes) ||
    maxBytes <= 0 ||
    file.size <= maxBytes
  ) {
    return null;
  }

  return {
    maxBytes,
    sizeBytes: file.size,
  };
}

export class MediaUploadFileTooLargeError extends Error {
  readonly maxBytes: number;
  readonly sizeBytes: number;

  constructor(params: MediaUploadSizeViolation) {
    super('File size exceeds the allowed limit');
    this.name = 'MediaUploadFileTooLargeError';
    this.maxBytes = params.maxBytes;
    this.sizeBytes = params.sizeBytes;
  }
}

export {
  MediaUploadFileTooLargeError as WorkspaceMediaUploadFileTooLargeError,
};

export async function uploadMediaInputFile(params: {
  file: File;
  folder?: string;
  maxBytes?: number;
  guestTask?: MediaUploadGuestTask;
  onProgress?: (progress: BrowserUploadProgress) => void;
}) {
  const sizeViolation = resolveMediaUploadSizeViolation(
    params.file,
    params.maxBytes
  );
  if (sizeViolation) {
    throw new MediaUploadFileTooLargeError(sizeViolation);
  }

  const upload = params.onProgress
    ? await uploadFileFromBrowser(
        params.file,
        params.folder,
        params.guestTask,
        { onProgress: params.onProgress }
      )
    : await uploadFileFromBrowser(params.file, params.folder, params.guestTask);

  return {
    ...upload,
    uploadedFile: params.file,
  };
}

export function normalizeWorkspaceTimedMediaBrowserFile(file: File) {
  const canonicalMimeType = resolveWorkspaceTimedMediaFileMimeType(file);
  if (!canonicalMimeType) {
    throw new Error('File type not supported');
  }

  return file.type === canonicalMimeType
    ? file
    : new File([file], file.name, {
        type: canonicalMimeType,
        lastModified: file.lastModified,
      });
}

export async function uploadWorkspaceMediaInputFile(params: {
  file: File;
  folder?: string;
  maxBytes?: number;
  supportedMimeTypes?: readonly string[];
  onProgress?: (progress: BrowserUploadProgress) => void;
}) {
  if (
    params.supportedMimeTypes?.length &&
    !isWorkspaceTimedMediaFileAccepted({
      file: params.file,
      supportedMimeTypes: params.supportedMimeTypes,
    })
  ) {
    throw new Error('File type not supported');
  }

  const uploadFile = normalizeWorkspaceTimedMediaBrowserFile(params.file);
  const upload = await uploadMediaInputFile({
    file: uploadFile,
    folder: params.folder,
    maxBytes: params.maxBytes,
    onProgress: params.onProgress,
  });

  return {
    ...upload,
    uploadedFile: params.file,
  };
}
